import { useEffect, useMemo, useState } from 'react';
import { IonContent, IonPage } from '@ionic/react';
import { useHistory, useParams } from 'react-router-dom';

import '../pm/theme.css';
import { AppUser, Team, TeamMembership } from '../pm/types';
import { displayName } from '../pm/constants';
import {
  deleteTeam,
  subscribeAllUsers,
  subscribeTeamMembers,
  subscribeTeams,
} from '../pm/firebase';
import { getCurrentUser, readUser } from '../firebase';
import { timeAgo } from '../pm/timeAgo';
import UserAvatar from '../components/pm/UserAvatar';
import RoleBadge from '../components/pm/RoleBadge';
import InviteMemberModal from '../components/pm/InviteMemberModal';
import ManageMemberMenu from '../components/pm/ManageMemberMenu';

function formatMemberSince(ts: number): string {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleString(undefined, { month: 'short', year: 'numeric' });
}

const TeamDetail: React.FC = () => {
  const history = useHistory();
  const { teamId } = useParams<{ teamId: string }>();

  const [uid, setUid] = useState<string>('');
  const [isRRAdmin, setIsRRAdmin] = useState(false);
  const [authReady, setAuthReady] = useState(false);
  const [teams, setTeams] = useState<Team[]>([]);
  const [memberships, setMemberships] = useState<TeamMembership[]>([]);
  const [users, setUsers] = useState<AppUser[]>([]);
  const [showInvite, setShowInvite] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    getCurrentUser()
      .then(async (u: any) => {
        if (!active) return;
        setUid(u?.uid || '');
        if (u?.uid) {
          try {
            const data: any = await readUser(u.uid);
            if (!active) return;
            setIsRRAdmin(data?.restricted?.role === 'admin');
          } catch (e) { /* ignore */ }
        }
        if (active) setAuthReady(true);
      })
      .catch(() => { if (active) setAuthReady(true); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const unsubT = subscribeTeams(setTeams);
    const unsubM = subscribeTeamMembers(setMemberships);
    const unsubU = subscribeAllUsers(setUsers);
    return () => { unsubT(); unsubM(); unsubU(); };
  }, []);

  const team = useMemo(() => teams.find((t) => t.id === teamId), [teams, teamId]);
  const teamMemberships = useMemo(
    () => memberships
      .filter((m) => m.teamId === teamId)
      .sort((a, b) => a.addedAt - b.addedAt),
    [memberships, teamId],
  );
  const myMembership = teamMemberships.find((m) => m.userId === uid);
  const isTeamAdmin = myMembership?.role === 'admin';
  const canManage = isRRAdmin || isTeamAdmin;

  const excludeUserIds = useMemo(
    () => new Set(teamMemberships.map((m) => m.userId)),
    [teamMemberships],
  );

  async function handleDeleteTeam() {
    if (!team) return;
    if (!window.confirm(`Delete team "${team.name}" and remove all its members?`)) return;
    try {
      await deleteTeam(team.id, teamMemberships);
      history.push('/Teams');
    } catch (err: any) {
      setError(err?.message || 'Could not delete team.');
    }
  }

  if (!authReady) {
    return <IonPage><IonContent fullscreen><div className="pm-app"><div className="pm-loading">Loading…</div></div></IonContent></IonPage>;
  }

  if (!team) {
    return (
      <IonPage>
        <IonContent fullscreen>
          <div className="pm-app">
            <div className="pm-page">
              <div className="pm-breadcrumb">
                <button type="button" className="pm-link-button" onClick={() => history.push('/Teams')}>Teams</button>
              </div>
              <div className="pm-empty">
                <h3>Team not found</h3>
                <p>It may have been deleted, or you don't have access.</p>
                <button className="pm-button" onClick={() => history.push('/Teams')}>Back to teams</button>
              </div>
            </div>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  const seatsUsed = teamMemberships.length;
  const seatsRemaining = Math.max(0, team.seatLimit - seatsUsed);
  const atCapacity = seatsRemaining === 0;

  return (
    <IonPage>
      <IonContent fullscreen>
        <div className="pm-app">
          <div className="pm-page">
            <div className="pm-breadcrumb">
              <button type="button" className="pm-link-button" onClick={() => history.push('/Teams')}>Teams</button>
              {' / '}
              <span>{team.name}</span>
            </div>

            <div className="pm-header">
              <div className="pm-header-titles">
                <h1>Team members</h1>
                <p>
                  {seatsUsed} of {team.seatLimit} seats used.
                  {atCapacity ? ' Need more? Upgrade your plan or contact sales.' : ''}
                </p>
              </div>
              <div className="pm-header-actions">
                {canManage ? (
                  <button
                    className="pm-button pm-button-primary"
                    onClick={() => setShowInvite(true)}
                    disabled={atCapacity}
                    title={atCapacity ? 'Seat limit reached' : undefined}
                  >
                    + Invite member
                  </button>
                ) : null}
                {isRRAdmin ? (
                  <button className="pm-button pm-button-danger" onClick={handleDeleteTeam}>
                    Delete team
                  </button>
                ) : null}
              </div>
            </div>

            {error ? <div className="pm-error">{error}</div> : null}

            <div className="pm-member-table">
              <div className="pm-member-table-header">
                <div className="pm-member-col-member">MEMBER</div>
                <div className="pm-member-col-role">ROLE</div>
                <div className="pm-member-col-since">MEMBER SINCE</div>
                <div className="pm-member-col-actions" />
              </div>
              {teamMemberships.length === 0 ? (
                <div className="pm-member-empty">
                  No members yet. {canManage ? 'Click "Invite member" to add the first one.' : ''}
                </div>
              ) : (
                teamMemberships.map((m) => {
                  const u = users.find((x) => x.uid === m.userId);
                  const email = u?.email || `(unknown user · ${m.userId.slice(0, 6)})`;
                  return (
                    <div key={m.id} className="pm-member-row">
                      <div className="pm-member-col-member">
                        <UserAvatar email={email} size={32} />
                        <div className="pm-member-identity">
                          <div className="pm-member-name">{displayName(email)}</div>
                          <div className="pm-member-email">{email}</div>
                        </div>
                      </div>
                      <div className="pm-member-col-role">
                        <RoleBadge role={m.role} />
                      </div>
                      <div className="pm-member-col-since" title={new Date(m.addedAt).toLocaleString()}>
                        {formatMemberSince(m.addedAt)}
                        <span className="pm-member-since-rel"> · {timeAgo(m.addedAt)}</span>
                      </div>
                      <div className="pm-member-col-actions">
                        <ManageMemberMenu membership={m} disabled={!canManage} />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
        {showInvite ? (
          <InviteMemberModal
            teamId={team.id}
            currentUid={uid}
            allUsers={users}
            excludeUserIds={excludeUserIds}
            onClose={() => setShowInvite(false)}
          />
        ) : null}
      </IonContent>
    </IonPage>
  );
};

export default TeamDetail;
